import * as Yup from 'yup'
import React from 'react'
import { FormItemProps } from '@/components/Form/types'

import Preview3dPlugin from '@/components/ProductProfile/components/Preview3dPlugin'
import Preview2dPlugin from '@/components/ProductProfile/components/Preview2dPlugin'
import CallToAction from '@/components/ProductProfile/components/CallToAction'
import getPricingList from '@/services/PricingServices/getPricingList'
import getShapeProfiles from '@/services/ProductService/getShapeProfiles'
import getViewProfiles from '@/services/ProductService/getViewProfiles'
import { components } from 'react-select'
import store from '@/store'

interface ProductProfileFormItemsOptions {
	onAddViewProfile?: () => void
	onAddPricing?: () => void
	onAddShapeProfile?: () => void
}

const getViewProfileImage = (profile: any): string | null => {
	return profile?.data?.model_thumbnail_image || profile?.model_thumbnail_image || profile?.thumbnail_image_url || null
}

const ViewProfileImage = ({ profile, size = 'md' }: { profile: any; size?: 'sm' | 'md' }) => {
	const image = getViewProfileImage(profile)
	const className = size === 'sm' ? 'w-[28px] h-[28px] min-w-[28px]' : 'w-[36px] h-[36px] min-w-[36px]'

	return (
		<div className={`${className} rounded-sm bg-gray-100 border border-gray-200 overflow-hidden flex items-center justify-center`}>
			{image ? (
				<img src={image} alt={profile?.name || 'View profile'} className="w-full h-full object-contain" />
			) : (
				<span className="font-icon-regular text-gray-300 text-[16px]">image</span>
			)}
		</div>
	)
}

const ViewProfileOption = (props: any) => {
	const profile = props.data?.data

	return (
		<components.Option {...props}>
			<div className="flex items-center gap-sm min-w-0">
				<ViewProfileImage profile={profile} />
				<span className="truncate">{props.data?.label}</span>
			</div>
		</components.Option>
	)
}

const ViewProfileSingleValue = (props: any) => {
	const profile = props.data?.data

	return (
		<components.SingleValue {...props}>
			<div className="flex items-center gap-xs min-w-0">
				<ViewProfileImage profile={profile} size="sm" />
				<span className="truncate">{props.data?.label}</span>
			</div>
		</components.SingleValue>
	)
}

const pricingModelLabels: Record<string, string> = {
	fixed: 'Fixed price',
	section: 'Per section',
	element: 'Per element',
	size: 'Per size',
}

const pricingRuleLabels: Record<string, string> = {
	fixed_amount: 'Fixed amount',
	percentage_discount: 'Percentage discount',
	amount_discount: 'Amount discount',
	percentage_of_base: 'Percentage of base',
}

const getPricingValue = (value: any): string => {
	if (value == null || value === '') return '-'
	const numberValue = Number(value)
	if (!Number.isFinite(numberValue)) return String(value)
	return `$${numberValue.toLocaleString(undefined, { maximumFractionDigits: 2 })}`
}

const normalizePricingKey = (value: any): string => {
	return (typeof value === 'object' ? value?.value : value || '').toString().toLowerCase()
}

const getPricingRuleText = (rule: any): string | null => {
	const ruleKey = normalizePricingKey(rule?.price_rule)
	if (!ruleKey) return null

	const value = Number(rule?.value)
	const formattedValue = Number.isFinite(value) ? value.toLocaleString(undefined, { maximumFractionDigits: 2 }) : rule?.value

	if (ruleKey === 'fixed_amount') return `${getPricingValue(rule?.value)} fixed`
	if (ruleKey === 'percentage_discount') return `${formattedValue}% off`
	if (ruleKey === 'amount_discount') return `${getPricingValue(rule?.value)} off`
	if (ruleKey === 'percentage_of_base') return `${formattedValue}% of base`
	return `${pricingRuleLabels[ruleKey] || ruleKey.replace(/_/g, ' ')} ${formattedValue || ''}`.trim()
}

const PricingOption = (props: any) => {
	const { data } = props
	const pricing = data?.data || {}
	const modelKey = normalizePricingKey(pricing.price_model)
	const rules = pricing.rule_config?.rules || pricing.rules || []
	const subsequentRule = pricing.rule_config?.subsequent_rule || (
		pricing.price_rule ? { price_rule: pricing.price_rule, value: pricing.subsequentValue } : null
	)
	const subsequentText = getPricingRuleText(subsequentRule)
	const meta = [
		pricingModelLabels[modelKey] || modelKey || 'Pricing',
		`Base ${getPricingValue(pricing.price)}`,
		`Total ${getPricingValue(pricing.min_price_total)} - ${getPricingValue(pricing.max_price_total)}`,
		...(modelKey !== 'fixed' ? [`${rules.length} tier rule${rules.length === 1 ? '' : 's'}`] : []),
		...(modelKey !== 'fixed' && subsequentText ? [`Default ${subsequentText}`] : []),
	]

	return (
		<components.Option {...props}>
			<div className="flex flex-col gap-xs min-w-0">
				<span className="text-title-md text-gray-900 truncate">{data.label}</span>
				<span className="text-text-xs text-gray-400 truncate">{meta.join(' | ')}</span>
			</div>
		</components.Option>
	)
}

const getItems = (options?: ProductProfileFormItemsOptions): Array<FormItemProps> => [

	{
		name: 'name',
		label: 'Profile Name',
		help: 'Enter a clear internal name for this product profile so it can be identified when managing the product.',
		type: 'Text',
		classNames: { container: 'col-span-6 ' },
		validation: Yup.string().required('Required')
	},
	{
		name: 'profileType',
		label: 'Profile Style',
		help: 'Choose how this profile handles customization. Vector(B/W) is intended for engraved output, while Color is intended for full-color artwork.',
		type: 'Switch',
		classNames: { container: 'col-span-3 ' },
		itemOptions: {
			list: [
				{ value: 'laser', label: 'Vector(B/W)' },
				{ value: 'color', label: 'Color' },
			],
		},
		defaultValue: { value: 'laser', label: 'Vector(B/W)' },
		validation: Yup.object().required('Required').typeError('Required')
	},
	{
		name: 'profileView',
		label: 'Profile View',
		help: 'Choose how the product is presented and configured. Use 3D for a model-based preview or 2D for image-based product sections.',
		type: 'Switch',
		classNames: { container: 'col-span-3' },
		itemOptions: {
			list: [
				{ value: '3D', label: '3D' },
				{ value: '2D', label: '2D' },
			],
		},
		defaultValue: { value: '3D', label: '3D' },
		validation: Yup.object().required('Required').typeError('Required'),
	},
	{
		name: 'viewProfile',
		label: 'View Profile',
		help: 'Select the 3D view profile that provides the product model and its projection settings for this profile.',
		placeholder: 'Select a view profile',
		type: 'Select',
		itemOptions: {
			fetch: async () => {
				const profiles = await getViewProfiles()
				return profiles?.map((item: any) => ({
					label: item?.name,
					value: item?.id,
					data: item,
				})) || []
			},
			clearable: true,
			features: { needToUpdateOnOpen: true },
			components: {
				Option: ViewProfileOption,
				SingleValue: ViewProfileSingleValue,
				...(options?.onAddViewProfile && {
					MenuList: (props: any) => (
						<components.MenuList {...props}>
							<div
								className="flex items-center gap-xs px-md py-sm text-text-md text-primary-500 hover:bg-gray-100 cursor-pointer border-b border-gray-200"
								onMouseDown={(e) => {
									e.preventDefault()
									e.stopPropagation()
									props.selectProps.onMenuClose()
									options.onAddViewProfile?.()
								}}
							>
								<span className="font-icon-solid text-[12px]">+</span>
								New View Profile
							</div>
							{props.children}
						</components.MenuList>
					),
				}),
			},
		},
		classNames: { container: 'col-span-6' },
		condition: (data: any) => data?.profileView?.value === '3D',
	},
	{
		name: 'glb',
		type: 'Hidden',
		classNames: { container: 'hidden' },
	},

	{
		name: 'productImage',
		type: 'Hidden',
		classNames: { container: 'hidden' },
	},
	{
		name: 'targetObjectWidth',
		type: 'Hidden',
		defaultValue: 200,
	},
	{
		name: 'targetObjectHeight',
		type: 'Hidden',
		defaultValue: 200,
	},

	{
		name: 'shapeProfile',
		label: 'Shape Profile',
		help: 'Select the shape profile used by the machine when preparing this product for processing.',
		placeholder: 'Select existing shape profile',
		type: 'Select',
		itemOptions: {
			fetch: async () => {
				const profiles = await getShapeProfiles()
				return profiles?.map((item: any) => ({
					label: item?.name,
					value: item?.id,
					data: item,
				})) || []
			},
			clearable: true,
			features: { needToUpdateOnOpen: true },
			components: {
				...(options?.onAddShapeProfile && {
					MenuList: (props: any) => (
						<components.MenuList {...props}>
							<div
								className="flex items-center gap-xs px-md py-sm text-text-md text-primary-500 hover:bg-gray-100 cursor-pointer border-b border-gray-200"
								onMouseDown={(e) => {
									e.preventDefault()
									e.stopPropagation()
									props.selectProps.onMenuClose()
									options.onAddShapeProfile?.()
								}}
							>
								<span className="font-icon-solid text-[12px]">+</span>
								New Shape Profile
							</div>
							{props.children}
						</components.MenuList>
					),
				}),
			},
		},
		classNames: { container: 'col-span-6' },
		condition: (data: any) => store.companySetting?.machine === 'neatolabs',
	},

	{
		name: 'priceModel',
		label: 'Pricing Model',
		help: 'Select the pricing configuration used to calculate the customization price for this product profile.',
		placeholder: 'Select pricing model',
		type: 'Select',
		itemOptions: {
			fetch: async () => {
				return await getPricingList()
			},
			features: { needToUpdateOnOpen: true },
			components: {
				Option: PricingOption,
				...(options?.onAddPricing && {
					MenuList: (props: any) => (
						<components.MenuList {...props}>
							<div
								className="flex items-center gap-xs px-md py-sm text-text-md text-primary-500 hover:bg-gray-100 cursor-pointer border-b border-gray-200"
								onMouseDown={(e) => {
									e.preventDefault()
									e.stopPropagation()
									props.selectProps.onMenuClose()
									options.onAddPricing?.()
								}}
							>
								<span className="font-icon-solid text-[12px]">+</span>
								New Pricing
							</div>
							{props.children}
						</components.MenuList>
					),
				}),
			},
		},
		validation: Yup.object().required('Required').typeError('Required'),
		classNames: { container: 'col-span-6' },
		condition: (data: any) => {
			const is2D = data?.profileView?.value === '2D'
			if (is2D) return true
			return data?.glb?.loaded
		},
	},



	{
		name: 'sections',
		type: 'Preview',
		itemOptions: {
			features: { fullWidthRow: true },
			preview: (props: any) => {
				const is2D = props.formData?.profileView?.value === '2D'
				if (is2D) {
					return <Preview2dPlugin {...props} />
				}
				return <Preview3dPlugin {...props} />
			},
		},
		classNames: { container: 'col-span-12 ' },
		condition: (data: any) => {
			const is2D = data?.profileView?.value === '2D'
			if (is2D) return true
			return data?.glb?.loaded
		},
	},






	{
		name: 'callToAction',
		type: 'Preview',
		itemOptions: {
			features: { fullWidthRow: true },
			preview: CallToAction,
		},
		classNames: { container: 'col-span-12 ' },
		condition: (data: any) => {
			const is2D = data?.profileView?.value === '2D'
			if (is2D) return true
			return data?.glb?.loaded
		},
	},



	{
		name: 'product',
		type: 'Hidden',
		classNames: { container: 'hidden' },
	},
	{
		name: 'lights',
		type: 'Hidden',
		classNames: { container: 'hidden' },
	},
	{
		name: 'profileData',
		type: 'Hidden',
		classNames: { container: 'hidden' },
	},
	{
		name: 'id',
		type: 'Hidden',
		classNames: { container: 'hidden' },
	},



]


/* /* formData?.["glb"] ?
    <Preview3dPlugin file={formData.glb.string} />
/* : null*/
export default getItems
